import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User } from '../../services/authService';
import { NotesService, Note as NoteType } from '../../services/notesService';
import BlockEditor from './BlockEditor';
import BlockEditorErrorBoundary from './BlockEditorErrorBoundary';
import { useBlockManager } from '../../hooks/useBlockManager';
import { BlockContainer } from '../../services/blockManager';
import './Note.css';

interface NoteProps {
  user: User | null;
  onToggleSidebar: () => void;
}

const Note: React.FC<NoteProps> = ({ user, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { noteId } = useParams<{ noteId: string }>();
  const [note, setNote] = useState<NoteType | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [showCreateConfirm, setShowCreateConfirm] = useState(false);
  const [noteNotFound, setNoteNotFound] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFullBlockMode, setIsFullBlockMode] = useState(false);
  
  // Use refs to maintain stable references for callbacks
  const noteRef = useRef<NoteType | null>(null);
  const noteTitleRef = useRef<string>('');
  const menuRef = useRef<HTMLDivElement>(null);

  // Get user ID
  const getUserId = (): number => {
    return user?.id || 0;
  };

  // Block management with standardized hook
  const {
    blockManager,
    contentBlocks,
    syncStatus,
    loadBlocks,
    setContainerId
  } = useBlockManager({
    userId: getUserId(),
    containerType: 'note',
    containerId: note?.note_id,
    onContainerUpdate: async (container: BlockContainer) => {
      const currentNote = noteRef.current;
      if (currentNote) {
        const blocks = container.blocks.map((blockId, index) => ({
          block_id: blockId,
          order: index
        }));

        await NotesService.updateNote(currentNote.note_id, {
          title: noteTitleRef.current,
          blocks,
          properties: currentNote.properties
        });
      }
    }
  });

  // Check if there's history to go back to
  const canGoBack = (): boolean => {
    return window.history.length > 1;
  };

  // Handle smart back navigation
  const handleBack = () => {
    if (canGoBack()) {
      navigate(-1); // Go back in history
    } else {
      navigate('/notes'); // Fallback to notes list
    }
  };

  // Handle delete note
  const handleDeleteNote = async () => {
    if (!note || !noteId) return;
    
    if (window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      try {
        await NotesService.deleteNote(noteId);
        navigate('/notes');
      } catch (error: any) {
        console.error('Error deleting note:', error);
        setError('Failed to delete note');
      }
    }
    setIsMenuOpen(false);
  };


  // Create a new note
  const createNewNote = async (id: string) => {
    if (!user) return;

    setLoading(true);
    setError(null);
    setShowCreateConfirm(false);
    setNoteNotFound(false);

    try {
      const userId = getUserId();
      // Use the note ID as the title
      const noteData = await NotesService.createNoteWithId(userId, id, id, []);
      
      setNote(noteData);
      setNoteTitle(noteData.title);
      setContainerId(noteData.note_id);
      
      console.log('✅ Created new note:', { noteId: noteData.note_id, title: noteData.title });
    } catch (error: any) {
      console.error('Error creating note:', error);
      setError('Failed to create note');
    } finally {
      setLoading(false);
    }
  };

  // Load note by ID
  const loadNote = useCallback(async (id: string) => {
    if (!user) return;

    setLoading(true);
    setError(null);
    setNoteNotFound(false);

    try {
      console.log('🔧 Loading note:', { id });
      const noteData = await NotesService.getNoteById(id, user.id);
      setNote(noteData);
      setNoteTitle(noteData.title);
      setContainerId(noteData.note_id);

      // Load the content blocks using block manager
      const blockIds = noteData.blocks.map(b => b.block_id);
      console.log('🔧 Loading content blocks:', { 
        noteId: noteData.note_id,
        blockIds,
        noteBlocks: noteData.blocks 
      });
      
      await loadBlocks(blockIds);
    } catch (error: any) {
      console.error('Error loading note:', error);
      
      // Check if it's a 404 or similar "not found" error
      if (error.status === 404 || error.message?.includes('not found') || error.message?.includes('does not exist')) {
        setNoteNotFound(true);
        setShowCreateConfirm(true);
        setError(null); // Clear error since we're showing confirmation
      } else {
        setError('Failed to load note');
      }
      
      // Set empty state on error
      setNote(null);
    } finally {
      setLoading(false);
    }
  }, [user, setContainerId, loadBlocks]);

  // Save note title (block structure is handled by BlockManager)
  const saveNote = useCallback(async (retryCount = 0): Promise<void> => {
    if (!user || !note) return;

    const maxRetries = 2;
    
    setSaving(true);
    setError(null);

    try {
      console.log('🔧 saveNote called for title update:', {
        noteId: note.note_id,
        noteTitle,
        retryCount
      });

      await NotesService.updateNote(note.note_id, {
        title: noteTitle,
        blocks: note.blocks, // Keep existing blocks structure
        properties: note.properties
      });

      setNote(prev => prev ? { ...prev, title: noteTitle } : null);
      
      console.log('✅ Note title saved successfully');
    } catch (error: any) {
      console.error(`Error saving note (attempt ${retryCount + 1}):`, error);
      
      // Retry logic for transient errors
      if (retryCount < maxRetries && (
        error.message?.includes('network') ||
        error.message?.includes('timeout') ||
        error.message?.includes('500') ||
        error.status >= 500
      )) {
        console.log(`🔄 Retrying save in ${(retryCount + 1) * 2} seconds...`);
        setTimeout(() => {
          saveNote(retryCount + 1);
        }, (retryCount + 1) * 2000); // Exponential backoff
        return;
      }
      
      setError(`Failed to save note${retryCount > 0 ? ` (after ${retryCount + 1} attempts)` : ''}`);
    } finally {
      setSaving(false);
    }
  }, [user, note, noteTitle]);

  // Block operations are now handled by BlockManager through the BlockEditor

  // Handle title edit
  const handleTitleSave = () => {
    if (noteTitle.trim() && noteTitle !== note?.title) {
      saveNote();
    }
    setIsEditing(false);
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setNoteTitle(note?.title || '');
      setIsEditing(false);
    }
  };

  // Note structure updates are now handled by BlockManager through onContainerUpdate callback

  // Keep refs synchronized with state
  useEffect(() => {
    noteRef.current = note;
  }, [note]);

  useEffect(() => {
    noteTitleRef.current = noteTitle;
  }, [noteTitle]);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Auto-save functionality for title changes
  useEffect(() => {
    if (!note) return;
    
    const autoSaveTimer = setTimeout(() => {
      // Only save the note title if we have title changes
      if (noteTitle !== note.title && noteTitle.trim()) {
        console.log('🔄 Auto-saving note title...');
        saveNote();
      }
    }, 5000); // Auto-save after 5 seconds of inactivity

    return () => clearTimeout(autoSaveTimer);
  }, [noteTitle, note?.title, note?.note_id, saveNote]);

  // Load note when component mounts or noteId changes
  useEffect(() => {
    if (noteId && user) {
      loadNote(noteId);
    }
  }, [noteId, user, loadNote]);

  // Clear error messages after timeout
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (!user) {
    return (
      <div className="note-container">
      <div className="note-header">
        <div className="header-left">
          <button className="menu-btn" onClick={onToggleSidebar}>☰</button>
          {canGoBack() && (
            <button className="note-back-btn" onClick={handleBack}>
              ← Back
            </button>
          )}
        </div>
      </div>
        <div className="note-auth-message">
          Please sign in to access your notes.
        </div>
      </div>
    );
  }

  if (!noteId) {
    return (
      <div className="note-container">
      <div className="note-header">
        <div className="header-left">
          <button className="menu-btn" onClick={onToggleSidebar}>☰</button>
          {canGoBack() && (
            <button className="note-back-btn" onClick={handleBack}>
              ← Back
            </button>
          )}
        </div>
      </div>
        <div className="note-error">
          No note ID provided.
        </div>
      </div>
    );
  }

  return (
    <div className="note-container">
      <div className="note-header">
        <div className="header-left">
          <button className="menu-btn" onClick={onToggleSidebar}>☰</button>
          {canGoBack() && (
            <button className="note-back-btn" onClick={handleBack}>
              ← Back
            </button>
          )}
        </div>
        <div className="note-header-actions">
          {saving && (
            <span className="note-saving-indicator">
              <div className="saving-spinner"></div>
              Saving...
            </span>
          )}
          {note && (
            <div className="note-menu-container" ref={menuRef}>
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)} 
                className="note-menu-btn"
                aria-label="More options"
              >
                ⋮
              </button>
              {isMenuOpen && (
                <div className="note-dropdown-menu">
                  <button 
                    onClick={() => {
                      setIsFullBlockMode(!isFullBlockMode);
                      setIsMenuOpen(false);
                    }} 
                    className="note-menu-item"
                  >
                    Full Block Mode: {isFullBlockMode ? 'ON' : 'OFF'}
                  </button>
                  <div className="note-menu-separator"></div>
                  <button 
                    onClick={handleDeleteNote} 
                    className="note-menu-item note-menu-item-danger"
                  >
                    Delete Note
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dedicated Title Section */}
      {note && (
        <div className="note-title-section">
          {isEditing ? (
            <input
              type="text"
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={handleTitleKeyPress}
              className="note-title-input-large"
              autoFocus
              placeholder="Enter note title..."
            />
          ) : (
            <h1 
              className="note-title-display-large" 
              onClick={() => setIsEditing(true)}
              title="Click to edit title"
            >
              {note?.title || 'Untitled Note'}
            </h1>
          )}
        </div>
      )}

      {error && (
        <div className="note-error">
          {error}
          <button onClick={() => noteId && loadNote(noteId)} className="note-retry-btn">
            Retry
          </button>
        </div>
      )}

      {/* Note Creation Confirmation Dialog */}
      {showCreateConfirm && noteNotFound && noteId && (
        <div className="note-create-confirm">
          <div className="create-confirm-content">
            <h3 className="create-confirm-title">Note Not Found</h3>
            <p className="create-confirm-message">
              The note with ID <code className="note-id-display">/{noteId}</code> doesn't exist.
              Would you like to create a new note with this ID?
            </p>
            <div className="create-confirm-actions">
              <button 
                className="create-confirm-btn create-confirm-yes"
                onClick={() => noteId && createNewNote(noteId)}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Note'}
              </button>
              <button 
                className="create-confirm-btn create-confirm-no"
                onClick={() => {
                  setShowCreateConfirm(false);
                  setNoteNotFound(false);
                  handleBack();
                }}
                disabled={loading}
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="note-content">
        {loading ? (
          <div className="note-loading">
            <div className="loading-spinner"></div>
            <p>Loading note...</p>
          </div>
        ) : (
          <div className="note-editor">
            <BlockEditorErrorBoundary>
              <BlockEditor
                blockManager={blockManager}
                blocks={contentBlocks}
                placeholder={`Start writing your note "${note?.title || 'Untitled'}" here...`}
                onSyncStatusChange={(status) => {}} // Sync status is handled by hook
                fullBlockMode={isFullBlockMode}
              />
            </BlockEditorErrorBoundary>
          </div>
        )}
      </div>

      {/* Sync Status Icon */}
      <div className={`note-sync-status-icon ${syncStatus}`}>
        {syncStatus === 'syncing' && <div className="note-sync-spinner">⟳</div>}
        {syncStatus === 'synced' && <div className="note-sync-check">✓</div>}
        {syncStatus === 'error' && <div className="note-sync-error">⚠</div>}
      </div>
    </div>
  );
};

export default Note;
